"use strict";
import { createTheme } from "@mui/material";
import WebFont from "webfontloader";
import embedBg from "./Assets/Images/incantor-bg.png";

const topBarGradient = "linear-gradient(90deg, #1f1f1f 0%, #2d3e50 50%, #2d5d85 100%)";

// New design tokens with a more descriptive structure
const themeTokens = {
  colors: {
    pageBackground: "linear-gradient(135deg, #ffffff, #f5f5f7)",
    sectionBackground: "#ffffff",
    headerText: "#000000",
    bodyText: "#333333",
    linkText: "#213572ff",
    accent: "#598294ff",
    mutedAccent: "#836a88ff",
    buttonPrimaryBg: "linear-gradient(90deg, #213572ff 0%, #598294ff 100%)",
    buttonPrimaryText: "#ffffff",
    buttonSecondaryBg: "#598294ff",
    buttonSecondaryText: "#ffffff",
    dropdownBg: "#f5f5f7",
    dropdownText: "#000000",
    dropdownHoverBg: "#e0e0e0",
    textFieldBg: "#f5f5f7",
    textFieldFocusBg: "#e0e0e0",
    topBarBg: "#1f1f1f",
    topBarGradient,
    topBarText: "#f5f5f5",
    audioPlayerBg: "#ffffff",
    tableHeaderBg: topBarGradient,
    tableHeaderText: "#f5f5f5",
    tableText: "#000000",
    tableBorder: "#BFBFBF",
    backgroundTintStart: "rgba(76, 162, 205, 0.2)",
    backgroundTintEnd: "rgba(103, 178, 111, 0.2)",
    backgroundWellColor: "rgba(0, 0, 0, 0.1)",
    backgroundVeil: "rgba(255, 255, 255, 0.4)",
  },
  fonts: {
    heading: '"Funnel Sans", sans-serif',
    body: '"Funnel Sans", sans-serif',
  },
  weights: {
    heading: 700,
    navigation: 500,
    body: 400,
  },
  radius: {
    md: "9px",
  },
};

// Reusable style snippets for list screens
export const searchBarSx = {
  width: '100%',
  mb: '20px',
};

export const nameCellSx = {
    fontWeight: 600,
    maxWidth: '220px',
};

export const selectMinWidthSx = {
  minWidth: '200px',
};

export const backgroundWellSx = {
    // The first layer is a white veil that lightens the textured background underneath.
    backgroundImage: "linear-gradient(var(--background-veil), var(--background-veil)), repeating-linear-gradient(45deg, rgba(255, 255, 255, 0.1) 0, rgba(255, 255, 255, 0.1) 1px, transparent 1px, transparent 3px), linear-gradient(to bottom, var(--background-tint-start), var(--background-tint-end)), var(--embed-bg)",
    backgroundSize: "auto, auto, 100% 100vh, cover",
    backgroundAttachment: "fixed, fixed, fixed, fixed",
    backgroundColor: "var(--background-well-color)",
    backgroundBlendMode: "normal, normal, normal, multiply",
};

// Expose theme tokens as CSS variables so plain CSS files can use them
const applyCssVariables = () => {
  const root = document.documentElement;
  root.style.setProperty("--color-page-bg", themeTokens.colors.pageBackground);
  root.style.setProperty("--color-section-bg", themeTokens.colors.sectionBackground);
  root.style.setProperty("--color-header-text", themeTokens.colors.headerText);
  root.style.setProperty("--color-body-text", themeTokens.colors.bodyText);
  root.style.setProperty("--color-link-text", themeTokens.colors.linkText);
  root.style.setProperty("--color-accent", themeTokens.colors.accent);
  root.style.setProperty("--color-muted-accent", themeTokens.colors.mutedAccent);
  root.style.setProperty("--color-button-primary-bg", themeTokens.colors.buttonPrimaryBg);
  root.style.setProperty("--color-button-primary-text", themeTokens.colors.buttonPrimaryText);
  root.style.setProperty("--color-button-secondary-bg", themeTokens.colors.buttonSecondaryBg);
  root.style.setProperty("--color-button-secondary-text", themeTokens.colors.buttonSecondaryText);
  root.style.setProperty("--color-dropdown-bg", themeTokens.colors.dropdownBg);
  root.style.setProperty("--color-dropdown-text", themeTokens.colors.dropdownText);
  root.style.setProperty("--color-dropdown-hover-bg", themeTokens.colors.dropdownHoverBg);
  root.style.setProperty("--color-textfield-bg", themeTokens.colors.textFieldBg);
  root.style.setProperty("--color-textfield-focus-bg", themeTokens.colors.textFieldFocusBg);
  root.style.setProperty("--color-topbar-bg", themeTokens.colors.topBarBg);
  root.style.setProperty("--color-topbar-gradient", themeTokens.colors.topBarGradient);
  root.style.setProperty("--color-topbar-text", themeTokens.colors.topBarText);
  root.style.setProperty("--color-audioplayer-bg", themeTokens.colors.audioPlayerBg);
  root.style.setProperty("--color-table-header-bg", themeTokens.colors.tableHeaderBg);
  root.style.setProperty("--color-table-header-text", themeTokens.colors.tableHeaderText);
  root.style.setProperty("--color-table-text", themeTokens.colors.tableText);
  root.style.setProperty("--color-table-border", themeTokens.colors.tableBorder);
  root.style.setProperty("--background-tint-start", themeTokens.colors.backgroundTintStart);
  root.style.setProperty("--background-tint-end", themeTokens.colors.backgroundTintEnd);
  root.style.setProperty("--background-well-color", themeTokens.colors.backgroundWellColor);
  root.style.setProperty("--background-veil", themeTokens.colors.backgroundVeil);
  root.style.setProperty("--embed-bg", `url("${embedBg}")`);
  root.style.setProperty("--font-heading", themeTokens.fonts.heading);
  root.style.setProperty("--font-body", themeTokens.fonts.body);
  root.style.setProperty("--weight-heading", themeTokens.weights.heading);
  root.style.setProperty("--weight-nav", themeTokens.weights.navigation);
  root.style.setProperty("--weight-body", themeTokens.weights.body);
  root.style.setProperty("--font-size-header", "24px");
  root.style.setProperty("--font-size-normal", "16px");
  root.style.setProperty("--radius-md", themeTokens.radius.md);
};

// Load Google fonts dynamically so font configuration lives with theme
export const loadFonts = () => {
  WebFont.load({
    google: {
      families: ["Funnel Sans:400,500,700"],
    },
  });
};

const createAppTheme = () => {
  applyCssVariables();
  const {
    colors: {
      accent,
      linkText,
      mutedAccent,
      bodyText,
      headerText,
      dropdownBg,
      dropdownText,
      dropdownHoverBg,
      textFieldBg,
      textFieldFocusBg,
      tableHeaderBg,
      tableHeaderText,
      tableText,
      tableBorder,
      sectionBackground,
      buttonPrimaryBg,
      buttonPrimaryText,
    buttonSecondaryBg,
    buttonSecondaryText,
  },
  fonts: { body: textFont },
  weights: { heading: headingWeight, navigation: navWeight, body: bodyWeight },
  radius: { md: borderRadius },
} = themeTokens;

  return createTheme({
    palette: {
      primary: {
        light: accent,
        main: linkText,
        contrastText: "#FFF",
      },
      secondary: {
        light: mutedAccent,
        main: mutedAccent,
        contrastText: "#000",
      },
      text: {
        primary: bodyText,
      },
      background: {
        default: themeTokens.colors.pageBackground,
        paper: sectionBackground,
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: textFont,
      h1: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      h2: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      h3: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      h4: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      h5: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      h6: {
        fontWeight: headingWeight,
        color: headerText,
        fontSize: "var(--font-size-header)",
      },
      button: { fontWeight: navWeight, fontSize: "var(--font-size-normal)" },
      subtitle1: { fontWeight: navWeight, fontSize: "var(--font-size-normal)" },
      body1: {
        fontWeight: bodyWeight,
        color: bodyText,
        fontSize: "var(--font-size-normal)",
      },
      body2: {
        fontWeight: bodyWeight,
        color: bodyText,
        fontSize: "var(--font-size-normal)",
      },
  },
  shape: {
    borderRadius: parseInt(borderRadius, 10),
  },
  components: {
      MuiSelect: {
        styleOverrides: {
          root: {
            backgroundColor: dropdownBg,
            color: dropdownText,
            border: "2px solid #BFBFBF",
            fontFamily: textFont,
            fontWeight: "500",
            borderRadius: borderRadius,
            "&:hover": {
              backgroundColor: dropdownHoverBg,
              color: dropdownText,
            },
            "& fieldset": {
              borderColor: "transparent",
            },
            "& .MuiSelect-select": {
              padding: "4px 8px",
            },
          },
          icon: {
            color: dropdownText,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: dropdownBg,
            color: dropdownText,
            fontFamily: textFont,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontFamily: textFont,
            backgroundColor: `${dropdownBg} !important`,
            color: `${dropdownText} !important`,
            padding: "4px 8px",
            minHeight: "auto",
            "&:hover": {
              backgroundColor: `${dropdownHoverBg} !important`,
              color: `${dropdownText} !important`,
            },
            "&:focus": {
              backgroundColor: `${dropdownHoverBg} !important`,
              color: `${dropdownText} !important`,
            },
            "&.Mui-selected": {
              backgroundColor: `${dropdownHoverBg} !important`,
              color: `${dropdownText} !important`,
            },
            "&.Mui-selected:hover": {
              backgroundColor: `${dropdownHoverBg} !important`,
              color: `${dropdownText} !important`,
            },
            "&.Mui-disabled": {
              backgroundColor: `${sectionBackground} !important`,
              color: `${dropdownText} !important`,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            background: textFieldBg,
            "&.Mui-focused": {
              background: textFieldFocusBg,
            },
          },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: {
            border: `1px solid ${tableBorder}`,
            borderCollapse: 'separate',
            borderSpacing: 0,
            borderRadius: borderRadius,
            overflow: 'hidden',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: tableText,
            textAlign: "center",
            background: sectionBackground,
            fontFamily: textFont,
            fontWeight: "500",
            border: `1px solid ${tableBorder}`,
          },
          head: {
            color: tableHeaderText,
            fontWeight: "bold",
            background: tableHeaderBg,
            fontFamily: textFont,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            background: buttonPrimaryBg,
            color: buttonPrimaryText,
            '&:hover': {
              background: accent,
            },
          },
          containedSecondary: {
            background: buttonSecondaryBg,
            color: buttonSecondaryText,
            '&:hover': {
              background: accent,
            },
          },
        },
      },
      // Standardize tooltip styling
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: dropdownBg,
            color: dropdownText,
            fontSize: "var(--font-size-normal)",
            border: `1px solid ${tableBorder}`,
          },
          arrow: {
            color: dropdownBg,
          },
        },
      },
    },
  });
};

export default createAppTheme;
